// src/pages/admin/partials/TermsEditor.js

import React, { useEffect, useState } from "react";
import axios from "axios";

const TermsEditor = () => {
  const [formData, setFormData] = useState({
    pageNavigation: "terms-and-conditions",
    title: "",
    description: "",
    sections: [{ question: "", answer: "" }],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isExisting, setIsExisting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const { data } = await axios.get("https://api.solarstation.in/static-page");

        const terms = data.find(
          (page) => page.pageNavigation === "terms-and-conditions"
        );

        setFormData(
          terms || {
            pageNavigation: "terms-and-conditions",
            title: "",
            description: "",
            sections: [{ question: "", answer: "" }],
          }
        );

        setIsExisting(!!terms);
      } catch (err) {
        console.error("Fetch error:", err);
        setIsExisting(false);
      }
    };

    fetchTerms();
  }, []);

  const handleSectionChange = (index, field, value) => {
    const sections = [...formData.sections];
    sections[index][field] = value;
    setFormData({ ...formData, sections });
  };

  const addSection = () => {
    setFormData({
      ...formData,
      sections: [...formData.sections, { question: "", answer: "" }],
    });
  };

  const removeSection = (index) => {
    const sections = [...formData.sections];
    sections.splice(index, 1);
    setFormData({ ...formData, sections });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setMessage("");
    try {
      if (isExisting) {
        await axios.put(
          "https://api.solarstation.in/static-page/terms-and-conditions",
          formData
        );
        setMessage("Terms updated!");
      } else {
        await axios.post("https://api.solarstation.in/static-page", formData);
        setMessage("Terms created!");
        setIsExisting(true);
      }
      setEditMode(false);
    } catch (err) {
      console.error(err);
      setMessage("Error saving Terms");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  return (
    <div>
      {/* Title */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Title</label>
        {editMode ? (
          <input
            type="text"
            name="title"
            className="w-full border rounded p-2"
            value={formData.title}
            onChange={handleChange}
          />
        ) : (
          <p className="bg-gray-50 border rounded p-2">{formData.title}</p>
        )}
      </div>

      {/* Description */}
      <div className="mb-4">
        <label className="block font-semibold mb-1">Description</label>
        {editMode ? (
          <textarea
            name="description"
            className="w-full border rounded p-2"
            value={formData.description}
            onChange={handleChange}
          />
        ) : (
          <p className="bg-gray-50 border rounded p-2 whitespace-pre-wrap">
            {formData.description}
          </p>
        )}
      </div>

      {/* Sections */}
      <h2 className="text-lg font-bold mb-2">Sections</h2>
      {formData.sections.map((section, index) => (
        <div
          key={index}
          className="mb-4 border border-gray-300 p-4 rounded shadow-sm"
        >
          <div className="mb-2">
            <label className="block text-sm font-semibold mb-1">Question</label>
            {editMode ? (
              <input
                type="text"
                className="w-full border rounded p-2"
                value={section.question}
                onChange={(e) =>
                  handleSectionChange(index, "question", e.target.value)
                }
              />
            ) : (
              <p className="bg-gray-50 border rounded p-2">
                {section.question}
              </p>
            )}
          </div>
          <div className="mb-2">
            <label className="block text-sm font-semibold mb-1">Answer</label>
            {editMode ? (
              <textarea
                className="w-full border rounded p-2"
                value={section.answer}
                onChange={(e) =>
                  handleSectionChange(index, "answer", e.target.value)
                }
              />
            ) : (
              <p className="bg-gray-50 border rounded p-2 whitespace-pre-wrap">
                {section.answer}
              </p>
            )}
          </div>
          {editMode && (
            <button
              type="button"
              onClick={() => removeSection(index)}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
            >
              Remove Section
            </button>
          )}
        </div>
      ))}

      {/* Action Buttons */}
      <div className="mt-6 flex gap-4 flex-wrap">
        {editMode ? (
          <>
            <button
              type="button"
              onClick={addSection}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Add Section
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className={`bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
            <button
              type="button"
              onClick={() => setEditMode(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setEditMode(true)}
            className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
          >
            Edit
          </button>
        )}
      </div>

      {message && <p className="mt-4 font-medium text-green-600">{message}</p>}
    </div>
  );
};

export default TermsEditor;
